import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const defaultOrder = () => {
  return {
    _id: '_new',
    number: '_new',
    supplier: null, // Will be required, but initialized as null for form handling
    items: [],
    deliveryLocation: {
      storage: null,
      project: null,
    },
    orderState: 'DRAFT',
    orderType: 'PURCHASE',
    priority: 'NORMAL',
    submittedToSupplierDate: null,
    expectedDeliveryDate: null,
    actualDeliveryDate: null,
    totalCost: null,
    notes: '',
    project: null,
    createdBy: null,
    approvedBy: null,
  };
};

const getDefaultState = () => {
  return {
    orders: [],
    orderCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an order list view
 * used for paginated order queries
 * @type {DocumentNode}
 */
const ORDER_LIST_REQUEST_FIELDS = gql`
  fragment orderListFields on Order {
    _id
    number
    supplier {
      _id
      number
      name
    }
    supplierOrderNumber
    submittedToSupplierDate
    expectedDeliveryDate
    actualDeliveryDate
    orderState
    orderType
    priority
    totalCost
    project {
      id
      number
      customer {
        firstname
        lastname
      }
    }
    createdBy {
      _id
      firstname
      lastname
      email
    }
    updatedBy {
      _id
      firstname
      lastname
      email
    }
  }
`;

/**
 * Defines the fields which are requested for an order
 * used for all queries/mutations which return an order
 * @type {DocumentNode}
 */
const ORDER_REQUEST_FIELDS = gql`
  fragment orderFields on Order {
    _id
    number
    supplier {
      _id
      number
      name
    }
    supplierOrderNumber
    submittedToSupplierDate
    expectedDeliveryDate
    actualDeliveryDate
    orderState
    orderType
    priority
    totalCost
    notes
    orderExportAttachments {
      id
      path
      filename
      displayFilename
      size
      mimetype
    }
    orderExportBuildState
    project {
      id
      number
      customer {
        firstname
        lastname
        street
        streetNumber
        streetNumberSuffix
        city
        zip
      }
    }
    items {
      quantityOrdered
      quantityReceived
      unitPrice
      item {
        _id
        number
        description
        unit
        price
        priceCalculation {
          # Item pricing
          itemMarkup
          itemProfitMarkup
          itemCustomerPrice

          # Labor pricing
          laborCost
          laborMarkup
          laborProfitMarkup
          laborCustomerPrice

          # Total pricing
          totalCustomerPrice

          calculatedAt
        }
      }
    }
    deliveryLocation {
      storage {
        _id
        name
      }
      project {
        id
        number
        customer {
          firstname
          lastname
        }
      }
    }
    createdBy {
      _id
      firstname
      lastname
      email
    }
    approvedBy {
      _id
      firstname
      lastname
      email
    }
    updatedBy {
      _id
      firstname
      lastname
      email
    }
    createdAt
    updatedAt
  }
`;

/**
 * sanitize inputs and create payload to send to the graphql api
 * @param {*} order
 */
const createPayload = (order) => {
  const payload = {
    supplierOrderNumber: order.supplierOrderNumber,
    orderState: order.orderState,
    orderType: order.orderType,
    items: order.items.map((item) => ({
      item: item.item._id || item.item,
      quantityOrdered: item.quantityOrdered,
      quantityReceived: item.quantityReceived || 0,
      unitPrice: item.unitPrice || null,
    })),
    deliveryLocation: {
      storage: order.deliveryLocation.storage?._id || order.deliveryLocation.storage,
      project: order.deliveryLocation.project?._id || order.deliveryLocation.project,
    },
  };

  // Add supplier ID if available
  if (order.supplier) {
    payload.supplier = order.supplier._id;
  }

  // Add optional fields only if they have values
  if (order.project) {
    payload.project = order.project.id || null;
  }
  if (order.expectedDeliveryDate) {
    payload.expectedDeliveryDate = order.expectedDeliveryDate;
  }
  if (order.actualDeliveryDate) {
    payload.actualDeliveryDate = order.actualDeliveryDate;
  }
  if (order.priority) {
    payload.priority = order.priority;
  }
  if (order.totalCost !== null && order.totalCost !== undefined) {
    payload.totalCost = order.totalCost;
  }
  if (order.notes) {
    payload.notes = order.notes;
  }
  if (order.approvedBy) {
    payload.approvedBy = order.approvedBy._id || order.approvedBy;
  }
  if (order.submittedToSupplierDate) {
    payload.submittedToSupplierDate = order.submittedToSupplierDate;
  }

  console.log(payload);

  return payload;
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered order list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchOrdersPaginated({ commit, dispatch }, { pagination, sorting, filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query ordersPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: OrderFilterInput
          ) {
            ordersPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
              orders {
                ...orderListFields
              }
              orderCount
            }
          }
          ${ORDER_LIST_REQUEST_FIELDS}
        `,
        variables: {
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      commit('SET_ORDERS', response.data.ordersPaginated.orders);
      commit('SET_ORDER_COUNT', response.data.ordersPaginated.orderCount);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  async fetchOrderByNumber({ commit, dispatch }, orderNumber) {
    try {
      // console.log(orderNumber);
      const response = await apolloClient.query({
        query: gql`
          query orderByNumber($orderNumber: Int!) {
            orderByNumber(orderNumber: $orderNumber) {
              ...orderFields
            }
          }
          ${ORDER_REQUEST_FIELDS}
        `,
        variables: {
          orderNumber: parseInt(orderNumber),
        },
      });
      commit('ADD_OR_UPDATE_ORDER', response.data.orderByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async createOrder({ commit, dispatch }, order) {
    const payload = createPayload(order);

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation createOrder($payload: OrderInput!) {
            createOrder(orderInput: $payload) {
              ...orderFields
            }
          }
          ${ORDER_REQUEST_FIELDS}
        `,
        variables: {
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Material gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_ORDER', '_new'); // remove temporary _new order
      commit('ADD_OR_UPDATE_ORDER', response.data.createOrder);
      return response.data.createOrder.number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async updateOrder({ commit, dispatch }, order) {
    const payload = createPayload(order);
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateOrder($orderNumber: Int!, $payload: OrderInput!) {
            updateOrder(orderNumber: $orderNumber, orderInput: $payload) {
              ...orderFields
            }
          }
          ${ORDER_REQUEST_FIELDS}
        `,
        variables: {
          orderNumber: order.number,
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Material aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_ORDER', response.data.updateOrder);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async deleteOrder({ commit, dispatch }, orderNumber) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteOrder($orderNumber: Int!) {
            deleteOrder(orderNumber: $orderNumber)
          }
        `,
        variables: {
          orderNumber: orderNumber,
        },
      });
      dispatch('message', {
        message: 'Material gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_ORDER', orderNumber);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new order object in store and return it
   * @param {*} param0
   * @param {*} order
   */
  async initOrder({ commit }) {
    commit('ADD_OR_UPDATE_ORDER', defaultOrder());
  },

  orderLocalUpdate({ commit }, order) {
    commit('ADD_OR_UPDATE_ORDER', order);
  },

  orderCleanup({ commit }) {
    commit('REMOVE_ORDER', '_new');
  },

  /**
   * Request an async build of a single order export
   * @param {*} context
   * @param {*} payload - contains orderNumber
   */
  async buildSingleOrderExport({ commit, dispatch }, { orderNumber }) {
    if (!orderNumber) {
      throw new Error('OrderNumber is undefined or null');
    }

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildSingleOrderExport($orderNumber: Int!) {
            buildSingleOrderExport(orderNumber: $orderNumber)
          }
        `,
        variables: {
          orderNumber,
        },
      });
      commit('UPDATE_ORDER_EXPORT_BUILD_STATE', {
        orderExportBuildState: response.data.buildSingleOrderExport,
        orderNumber: orderNumber,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Delete an attachment from an order
   * @param {*} param0
   * @param {*} payload - contains orderId, attachmentId, attachmentType
   */
  async deleteOrderAttachment({ commit, dispatch }, { orderId, attachmentId, attachmentType }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteOrderAttachment(
            $orderId: ID!
            $attachmentId: ID!
            $attachmentType: String!
          ) {
            deleteOrderAttachment(
              orderId: $orderId
              attachmentId: $attachmentId
              attachmentType: $attachmentType
            ) {
              ...orderFields
            }
          }
          ${ORDER_REQUEST_FIELDS}
        `,
        variables: {
          orderId,
          attachmentId,
          attachmentType,
        },
      });
      commit('ADD_OR_UPDATE_ORDER', response.data.deleteOrderAttachment);
      dispatch('message', {
        message: 'Anhang gelöscht!',
        type: 'success',
        lifetime: 2,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Fetch the build state and attachments for order export
   * @param {*} param0
   * @param {*} orderNumber
   */
  async fetchOrderExportAttachmentBuildState({ commit, dispatch }, orderNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query orderByNumber($orderNumber: Int!) {
            orderByNumber(orderNumber: $orderNumber) {
              _id
              number
              orderExportBuildState
              orderExportAttachments {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
            }
          }
        `,
        variables: {
          orderNumber: parseInt(orderNumber),
        },
      });
      console.log('fetchOrderExportAttachmentBuildState()', response.data.orderByNumber);
      commit('ADD_OR_UPDATE_ORDER', response.data.orderByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_ORDER_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_ORDERS: (state, orders) => (orders ? (state.orders = orders) : (state.orders = [])),
  ADD_ORDER: (state, order) => state.orders.push(order),
  ADD_OR_UPDATE_ORDER: (state, updatedOrder) => {
    const existingOrder = state.orders.find((order) => order._id === updatedOrder._id);
    if (existingOrder) {
      Object.assign(existingOrder, updatedOrder);
    } else {
      state.orders.push(updatedOrder);
    }
  },
  REMOVE_ORDER: (state, orderId) => {
    const index = state.orders.findIndex((order) => order._id === orderId);
    state.orders.splice(index, 1);
  },
  UPDATE_ORDER: (state, updatedOrder) => {
    const order = state.orders.find((order) => order._id === updatedOrder._id);
    Object.assign(order, updatedOrder);
  },
  SET_ORDER_COUNT: (state, orderCount) => (state.orderCount = orderCount),
  UPDATE_ORDER_EXPORT_BUILD_STATE: (state, { orderExportBuildState, orderNumber }) => {
    // Find the order and update its export build state
    const order = state.orders.find((order) => order.number === orderNumber);
    if (order) {
      order.orderExportBuildState = orderExportBuildState;
    }
  },
};

/**
 * Getters
 */
const getters = {
  getOrders: (state) => state.orders,
  getOrder: (state) => (orderId) => state.orders.find((order) => order._id == orderId),
  getOrdersByState: (state) => (orderState) =>
    state.orders.filter((order) => order.orderState == orderState),
  getOrderByNumber: (state) => (orderNumber) =>
    state.orders.find((order) => order.number == orderNumber),
  getOrderCount: (state) => state.orderCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
